/**
 * Global Navigation Component
 * Fixed top nav bar with links and mobile hamburger menu.
 * NOT shown on /event/{CODE} pages (projectable full-screen).
 */

export interface NavConfig {
  currentPath: string;
}

function getSvgIcon(name: string): string {
  switch (name) {
    case 'home':
      return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>';
    case 'compass':
      return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>';
    case 'calendar':
      return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>';
    case 'map-pin':
      return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>';
    case 'external':
      return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>';
    default:
      return '';
  }
}

export function renderNavigation(config: NavConfig): string {
  const { currentPath } = config;

  const links = [
    { href: '/', label: 'Home', icon: 'home', exact: true },
    { href: '/discover', label: 'Sessions', icon: 'compass', exact: false },
    { href: '/events', label: 'Events', icon: 'calendar', exact: false },
    { href: '/discover/map', label: 'Map', icon: 'map-pin', exact: true },
  ];

  const navLinks = links.map((link) => {
    const isActive = link.exact
      ? currentPath === link.href
      : currentPath.startsWith(link.href);
    return `
      <a href="${link.href}"
         class="nav-link ${isActive ? 'nav-link--active' : ''}"
         ${isActive ? 'aria-current="page"' : ''}>
        ${getSvgIcon(link.icon)}
        <span class="nav-link-label">${link.label}</span>
      </a>
    `;
  }).join('');

  return `
    <nav class="global-nav" role="navigation" aria-label="Main navigation">
      <div class="nav-inner">
        <a href="/" class="nav-brand" aria-label="Ecstatic Oracle Home">
          <span class="nav-brand-icon">&#x1F52E;</span>
          <span class="nav-brand-text">Ecstatic Oracle</span>
        </a>
        <div class="nav-links" id="nav-links">
          ${navLinks}
          <span class="nav-divider"></span>
          <a href="https://ecstaticoracle.dance/events/" class="nav-link nav-link--external" target="_blank" rel="noopener">
            ${getSvgIcon('external')}
            <span class="nav-link-label">Website</span>
          </a>
        </div>
        <button class="nav-hamburger" id="nav-hamburger"
                aria-expanded="false" aria-controls="nav-links"
                aria-label="Toggle navigation menu">
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
        </button>
      </div>
    </nav>
    <div class="nav-spacer"></div>
  `;
}

export function initNavigationListeners(): void {
  const hamburger = document.getElementById('nav-hamburger');
  const navLinks = document.getElementById('nav-links');

  hamburger?.addEventListener('click', () => {
    const expanded = hamburger.getAttribute('aria-expanded') === 'true';
    hamburger.setAttribute('aria-expanded', String(!expanded));
    navLinks?.classList.toggle('nav-links--open');
    hamburger.classList.toggle('nav-hamburger--open');
  });

  // Close on link click (mobile)
  navLinks?.querySelectorAll('.nav-link').forEach((link) => {
    link.addEventListener('click', () => {
      navLinks.classList.remove('nav-links--open');
      hamburger?.classList.remove('nav-hamburger--open');
      hamburger?.setAttribute('aria-expanded', 'false');
    });
  });
}
